
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { createApiUrl } from "@/lib/api";

interface PhoneOTPLoginProps {
  onBack: () => void;
}

const PhoneOTPLogin = ({ onBack }: PhoneOTPLoginProps) => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleRoleBasedRedirection = (user: any) => {
    console.log('User data received:', user);
    console.log('User role:', user.role);
    
    switch (user.role) {
      case 'admin':
        console.log('Redirecting to admin dashboard');
        navigate('/admin/dashboard');
        break;
      case 'teacher':
        console.log('Redirecting to teacher dashboard');
        navigate('/teacher/dashboard');
        break;
      case 'student':
        console.log('Redirecting to student dashboard');
        navigate('/student/dashboard');
        break;
      default:
        console.log('Unknown role, redirecting to student dashboard by default');
        navigate('/student/dashboard');
    }
  };

  const handlePhonePasswordLogin = async () => {
    if (!phone || !password) {
      toast({
        title: "Missing Information",
        description: "Please enter both phone number and password",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
              const response = await fetch(createApiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phone.startsWith('+91') ? phone : `+91${phone}`,
          password: password
        }),
      });

      const data = await response.json();
      console.log('Phone login response:', data);

      if (response.ok) {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem(`${data.user.role}Data`, JSON.stringify(data.user));
        
        toast({
          title: "Login Successful!",
          description: `Welcome back, ${data.user.name}!`
        });

        handleRoleBasedRedirection(data.user);
      } else {
        throw new Error(data.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Phone login error:', error);
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "Please check your credentials and try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!phone || phone.length < 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
              const response = await fetch(createApiUrl('/auth/send-otp'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phone.startsWith('+91') ? phone : `+91${phone}`
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsOTPSent(true);
        toast({
          title: "OTP Sent!",
          description: data.message || `OTP sent to ${phone.startsWith('+91') ? phone : `+91${phone}`}`
        });
      } else {
        throw new Error(data.detail || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('OTP send error:', error);
      toast({
        title: "Failed to Send OTP",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter the 6-digit OTP",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
              const response = await fetch(createApiUrl('/auth/verify-otp'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          otp: otp,
          phone: phone.startsWith('+91') ? phone : `+91${phone}`
        }),
      });

      const data = await response.json();
      console.log('OTP verification response:', data);

      if (response.ok) {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem(`${data.user.role}Data`, JSON.stringify(data.user));
        
        toast({
          title: "Login Successful!",
          description: `Welcome back, ${data.user.name}!`
        });

        handleRoleBasedRedirection(data.user);
      } else {
        throw new Error(data.detail || 'OTP verification failed');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      toast({
        title: "Invalid OTP",
        description: error instanceof Error ? error.message : "Please enter the correct OTP",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="backdrop-blur-sm bg-white/90 shadow-2xl border-0">
      <CardHeader>
        <div className="flex items-center justify-center gap-3">
          <Phone className="w-6 h-6 text-indigo-600" />
          <div className="text-center">
            <CardTitle className="text-xl">Phone Login</CardTitle>
            <p className="text-sm text-gray-600">Login with your phone number</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!isOTPSent ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex">
                <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-gray-50">
                  +91
                </div>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter 10-digit mobile number"
                  className="rounded-l-none"
                  maxLength={10}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant={usePasswordLogin ? "outline" : "default"}
                onClick={() => setUsePasswordLogin(false)}
                className="flex-1"
                disabled={isLoading}
              >
                OTP Login
              </Button>
              <Button 
                variant={usePasswordLogin ? "default" : "outline"}
                onClick={() => setUsePasswordLogin(true)}
                className="flex-1"
                disabled={isLoading}
              >
                Password Login
              </Button>
            </div>

            {usePasswordLogin && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
              </div>
            )}
            
            <Button 
              onClick={usePasswordLogin ? handlePhonePasswordLogin : handleSendOTP} 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Please wait...' : (usePasswordLogin ? 'Login' : 'Send OTP')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                OTP sent to +91 {phone}
              </p>
            </div>

            <div>
              <Label>Enter 6-digit OTP</Label>
              <div className="flex justify-center mt-2">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} disabled={isLoading}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <Button 
              onClick={handleVerifyOTP} 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </Button>

            <div className="text-center">
              <Button 
                variant="link" 
                onClick={() => setIsOTPSent(false)}
                className="text-indigo-600"
                disabled={isLoading}
              >
                Back to Phone Login
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PhoneOTPLogin;
